import type { ShapeHandle, WallShape } from '@walls/store';
import {
  getRectangleAttributes,
  getShapeType,
  getTextAttributes,
} from '../../drawing/utils/shapeAttributes';
import {
  getLineBounds,
  hitTestLine,
  hitTestLineHandle,
  moveLineAttributes,
  resizeLineAttributes,
} from './lineGeometry';
import { measureTextShape } from './textGeometry';

export interface BoardPoint {
  x: number;
  y: number;
}

export interface ShapeBounds {
  height: number;
  width: number;
  x: number;
  y: number;
}

const borderTolerance = 4;
const selectedBorderWidth = 2;

export function getShapeBounds(
  shape: WallShape,
  context?: CanvasRenderingContext2D | null,
  zoom = 1
): ShapeBounds {
  const shapeType = getShapeType(shape);

  if (shapeType === 'line') {
    return getLineBounds(shape);
  }

  if (shapeType === 'text') {
    const measured = measureTextShape(shape, context);

    return {
      height: measured.height,
      width: measured.width,
      x: measured.x,
      y: measured.y,
    };
  }

  const attributes = getRectangleAttributes(shape);

  return {
    height: attributes.height + attributes.lineThickness,
    width: attributes.width + attributes.lineThickness,
    x: attributes.x - attributes.lineThickness / 2,
    y: attributes.y - attributes.lineThickness / 2,
  };
}

export function hitTestShape(
  shape: WallShape,
  point: BoardPoint,
  context?: CanvasRenderingContext2D | null,
  zoom = 1
) {
  const shapeType = getShapeType(shape);

  if (shapeType === 'line') {
    return hitTestLine(shape, point, zoom);
  }

  if (shapeType === 'text') {
    const bounds = getShapeBounds(shape, context, zoom);
    return point.x >= bounds.x - selectedBorderWidth &&
      point.x <= bounds.x + bounds.width + selectedBorderWidth &&
      point.y >= bounds.y - selectedBorderWidth &&
      point.y <= bounds.y + bounds.height + selectedBorderWidth;
  }

  const attributes = getRectangleAttributes(shape);

  if (!attributes.fillOpacity) {
    return false;
  }

  if (shapeType === 'circle') {
    const semiMajorAxis = attributes.width / 2;
    const semiMinorAxis = attributes.height / 2;
    const centerX = attributes.x + attributes.width / 2;
    const centerY = attributes.y + attributes.height / 2;
    const normalizedX = (point.x - centerX) / semiMajorAxis;
    const normalizedY = (point.y - centerY) / semiMinorAxis;

    return normalizedX * normalizedX + normalizedY * normalizedY <= 1;
  }

  return point.x >= attributes.x &&
    point.x <= attributes.x + attributes.width &&
    point.y >= attributes.y &&
    point.y <= attributes.y + attributes.height;
}

export function hitTestShapeHandle(shape: WallShape, point: BoardPoint, zoom = 1): ShapeHandle {
  if (getShapeType(shape) === 'line') {
    return hitTestLineHandle(shape, point);
  }

  if (getShapeType(shape) === 'text') {
    return null;
  }

  const attributes = getRectangleAttributes(shape);
  const tolerance = Math.floor(borderTolerance / zoom);
  const bounds = getShapeBounds(shape);

  if (point.x <= bounds.x + tolerance && point.x >= bounds.x - tolerance && point.y >= bounds.y && point.y <= bounds.y + bounds.height) {
    return 'left';
  }

  if (point.x >= bounds.x + bounds.width - tolerance && point.x <= bounds.x + bounds.width + tolerance && point.y >= bounds.y && point.y <= bounds.y + bounds.height) {
    return 'right';
  }

  if (point.y <= bounds.y + tolerance && point.y >= bounds.y - tolerance && point.x >= bounds.x && point.x <= bounds.x + bounds.width) {
    return 'top';
  }

  if (point.y >= bounds.y + bounds.height - tolerance && point.y <= bounds.y + bounds.height + tolerance && point.x >= bounds.x && point.x <= bounds.x + bounds.width) {
    return 'bottom';
  }

  // Keep attributes referenced so TS narrows this branch for rectangle-like shapes.
  void attributes;
  return null;
}

export function moveShapeAttributes(shape: WallShape, deltaX: number, deltaY: number) {
  const shapeType = getShapeType(shape);

  if (shapeType === 'line') {
    return moveLineAttributes(shape, deltaX, deltaY);
  }

  const attributes = shapeType === 'text' ? getTextAttributes(shape) : getRectangleAttributes(shape);

  return {
    x: attributes.x + deltaX,
    y: attributes.y + deltaY,
  };
}

export function resizeShapeAttributes(
  shape: WallShape,
  handle: Exclude<ShapeHandle, null>,
  deltaX: number,
  deltaY: number
) {
  if (handle === 'start' || handle === 'end') {
    return resizeLineAttributes(shape, handle, deltaX, deltaY);
  }

  const attributes = getRectangleAttributes(shape);

  switch (handle) {
    case 'left':
      return attributes.x + deltaX < attributes.x + attributes.width
        ? { x: attributes.x + deltaX, width: attributes.width - deltaX }
        : {};
    case 'right':
      return attributes.x + attributes.width + deltaX > attributes.x
        ? { width: attributes.width + deltaX }
        : {};
    case 'top':
      return attributes.y + deltaY < attributes.y + attributes.height
        ? { y: attributes.y + deltaY, height: attributes.height - deltaY }
        : {};
    case 'bottom':
      return attributes.y + attributes.height + deltaY > attributes.y
        ? { height: attributes.height + deltaY }
        : {};
  }
}
